import { useEffect, useState } from 'react'
import { m } from 'motion/react'
import { CircleAlert, CircleCheck, Fingerprint, LoaderCircle } from 'lucide-react'
import { useT } from '../lib/i18n.tsx'
import { Button } from './Button.tsx'
import { Sheet } from './Sheet.tsx'
import { cn, duration } from './util.ts'

/** A step-up lasts 15 minutes (docs/CONTRACTS.md section 7: session.step_up_until = now + 15 min). */
export const STEP_UP_MS = 15 * 60 * 1000

/** Remaining time as m:ss, ticking every second (display only; the server decides). */
export function useCountdown(until?: number): { left: number; text: string } {
  const [, tick] = useState(0)
  useEffect(() => {
    if (!until) return
    const timer = window.setInterval(() => tick((n) => n + 1), 1000)
    return () => window.clearInterval(timer)
  }, [until])
  const left = until ? Math.max(0, until - Date.now()) : 0
  const s = Math.ceil(left / 1000)
  return { left, text: `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
}

export interface StepUpSheetProps {
  open: boolean
  onClose: () => void
  /**
   * Runs the passkey step-up (P1 auth piece: step-up/begin, the browser's passkey prompt, step-up/finish).
   * Resolves when the server confirmed it; rejects when the person cancelled or the passkey failed.
   */
  onConfirm: () => Promise<void>
  /** When the current step-up ends (ms timestamp, from /api/me stepUpUntil); shows the countdown. */
  until?: number
  /** What the step-up unlocks; defaults to revealing register names. */
  reason?: string
}

/** The passkey step-up sheet with the 15-minute countdown. */
export function StepUpSheet({ open, onClose, onConfirm, until, reason }: StepUpSheetProps) {
  const t = useT()
  const [phase, setPhase] = useState<'ask' | 'verifying' | 'failed'>('ask')
  const { left, text } = useCountdown(until)
  const done = !!until && left > 0

  useEffect(() => {
    if (!open) setPhase('ask')
  }, [open])

  const verify = () => {
    setPhase('verifying')
    onConfirm().then(
      () => setPhase('ask'),
      () => setPhase('failed'),
    )
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      side="center"
      size="sm"
      title={done ? t('common.stepUp.doneTitle') : t('common.stepUp.title')}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose} data-autofocus>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon={Fingerprint} onClick={verify} loading={phase === 'verifying'} data-autofocus>
              {phase === 'verifying' ? t('common.stepUp.waiting') : t('common.stepUp.use')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <span
          className={cn(
            'relative mt-2 inline-flex size-20 items-center justify-center rounded-full ring-1 ring-inset',
            done
              ? 'bg-ok-soft text-ok ring-ok-line'
              : phase === 'failed'
                ? 'bg-danger-soft text-danger ring-danger-line'
                : 'bg-accent-soft text-accent-text ring-accent-line',
          )}
        >
          {phase === 'verifying' && (
            <m.span
              aria-hidden
              className="absolute inset-0 rounded-full ring-2 ring-accent"
              initial={{ scale: 1, opacity: 0.8 }}
              animate={{ scale: 1.35, opacity: 0 }}
              transition={{ duration: duration('slow') * 2.5, repeat: Infinity, ease: 'easeOut' }}
            />
          )}
          {done ? (
            <CircleCheck size={34} strokeWidth={1.5} aria-hidden />
          ) : phase === 'verifying' ? (
            <LoaderCircle size={34} strokeWidth={1.5} aria-hidden className="animate-spin" />
          ) : phase === 'failed' ? (
            <CircleAlert size={34} strokeWidth={1.5} aria-hidden />
          ) : (
            <Fingerprint size={34} strokeWidth={1.5} aria-hidden />
          )}
        </span>
        {done ? (
          <>
            <div role="timer" aria-live="off" className="font-mono text-2xl text-ink tabular">
              {text}
            </div>
            <p className="text-sm text-ink-muted">{t('common.stepUp.doneBody')}</p>
          </>
        ) : (
          <>
            <p className="text-base text-ink-muted">
              {reason ?? t('common.stepUp.reason')} {t('common.stepUp.window', { time: '15:00' })}
            </p>
            {phase === 'failed' && (
              <p role="alert" className="text-sm text-danger">
                {t('common.stepUp.failed')}
              </p>
            )}
          </>
        )}
      </div>
    </Sheet>
  )
}
